import React, { Suspense, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AuthContext } from "src/utils/AuthContext.js";
import { getPageRoute } from "src/services/Config.js";
import { customEvent } from "src/utils/gtag.js";
import { lazyWithRetry } from "src/utils/lazyWithRetry.js";
import {
  GOOGLE_CONNECT_STATES,
  turnOffGoogleConnectState,
} from "src/utils/googleConnect.js";
import {
  googleConnectInactiveInterstitialWillShow,
  googleConnectInactiveShownKey,
  INTERSTITIAL_KEYS,
  isSuppressedRoute,
  outrankedInterstitial,
  stampNow,
} from "src/utils/interstitial.js";


const GoogleConnectInactiveInterstitialDialog = lazyWithRetry(() =>
  import(
    "src/components/blocks/googleconnect/GoogleConnectInactiveInterstitialDialog.js"
  )
);

/**
 * Interstitial telling a lapsed Google Connect user their synced calendars are
 * paused, with the two ways forward: come back, or finish offboarding.
 */
export default function GoogleConnectInactiveInterstitial() {
  // Mounted at app root, so tolerate rendering outside AuthProvider.
  const { user, isLoading } = useContext(AuthContext) ?? {};
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const shownRef = useRef(false);
  const openedAtPathRef = useRef(null);
  // Latched for the load once a higher-ranked nudge is due: it stops reading as due
  // the moment it shows and stamps its fatigue key, and this one must still yield.
  const deferredRef = useRef(false);

  const onSuppressedRoute = isSuppressedRoute(location);
  const outranked = outrankedInterstitial(INTERSTITIAL_KEYS.GOOGLE_CONNECT_INACTIVE, user);

  useEffect(() => {
    if (outranked) deferredRef.current = true;
  }, [outranked]);

  useEffect(() => {
    if (isLoading || open || onSuppressedRoute || shownRef.current) return;
    // Evaluated inline rather than latched in state: a higher-ranked nudge's own
    // open-effect runs in this same commit, so a stale read would show both.
    const eligible =
      googleConnectInactiveInterstitialWillShow(user) &&
      !outranked &&
      !deferredRef.current;
    if (!eligible) return;
    openedAtPathRef.current = location.pathname;
    setOpen(true);
  }, [user, isLoading, onSuppressedRoute, open, location.pathname]);

  const handleShown = useCallback(() => {
    shownRef.current = true;
    stampNow(googleConnectInactiveShownKey(user?._id));
    customEvent({
      name: "google_connect_inactive_interstitial_shown",
      category: "GoogleConnect",
      label: "Google Connect Inactive Interstitial Shown",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        connection_state: GOOGLE_CONNECT_STATES.INACTIVE,
      },
    });
  }, [user?._id, user?.email]);

  useEffect(() => {
    if (!openedAtPathRef.current) return;
    if (openedAtPathRef.current === location.pathname) return;
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (user) return;
    shownRef.current = false;
    openedAtPathRef.current = null;
    deferredRef.current = false;
    setOpen(false);
  }, [user]);

  const track = (name) => {
    customEvent({
      name,
      category: "GoogleConnect",
      label: "Google Connect Inactive Interstitial",
      data: {
        user_id: user?._id,
        user_email: user?.email,
        connection_state: GOOGLE_CONNECT_STATES.INACTIVE,
      },
    });
  };

  const handleClose = () => {
    setOpen(false);
    track("google_connect_inactive_interstitial_dismissed");
  };

  const handleResubscribe = () => {
    setOpen(false);
    track("google_connect_inactive_interstitial_resubscribe_clicked");
    navigate(getPageRoute("upgrade", "UPGRADE_PLUS"), {
      state: { source: "google_connect_inactive_interstitial" },
    });
  };

  const handleOffboard = () => {
    setOpen(false);
    track("google_connect_inactive_interstitial_offboard_clicked");
    navigate(getPageRoute("help", "GOOGLE_CONNECT_OFF"), {
      state: turnOffGoogleConnectState(location),
    });
  };

  return (
    <>
      {open && user && (
        <Suspense fallback={null}>
          <GoogleConnectInactiveInterstitialDialog
            cancelledOnPurpose={Boolean(user.hasEndedVoluntaryCancellation)}
            onShown={handleShown}
            onClose={handleClose}
            onResubscribe={handleResubscribe}
            onOffboard={handleOffboard}
          />
        </Suspense>
      )}
    </>
  );
}
